import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Toast from '../components/Toast';
import api, { formatApiErrorDetail } from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * /featured/buy — Featured Slot purchase page.
 * Verified creators pay Points to pin their Clan to Home Top-5 for N days.
 * Backend at /api/featured/purchase already exists; this page completes the UX.
 */
export default function FeaturedPurchase() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [tiers, setTiers] = useState(null);
  const [mySlot, setMySlot] = useState(undefined);
  const [balance, setBalance] = useState(null);
  const [selected, setSelected] = useState(null); // days
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/featured/pricing').catch(() => ({ data: { tiers: [] } })),
      api.get('/featured/mine').catch(() => ({ data: { slot: null } })),
      api.get('/wallet/portfolio').catch(() => ({ data: { balance: null } })),
    ]).then(([p, m, w]) => {
      const list = p.data.tiers || [];
      setTiers(list);
      setMySlot(m.data.slot);
      setBalance(w.data.balance);
      // Default select the "best value" (middle) tier
      if (list.length >= 2) setSelected(list[Math.floor(list.length / 2)].days);
      else if (list.length === 1) setSelected(list[0].days);
    });
  }, []);

  const chosenTier = useMemo(
    () => tiers?.find((t) => Number(t.days) === Number(selected)),
    [tiers, selected],
  );
  const canAfford = chosenTier && balance !== null && balance >= Number(chosenTier.pts);

  const purchase = async () => {
    if (!chosenTier || submitting) return;
    setErr('');
    setSubmitting(true);
    try {
      const { data } = await api.post('/featured/purchase', { days: Number(chosenTier.days) });
      await refreshMe();
      setMySlot(data.slot);
      // Re-fetch balance
      const w = await api.get('/wallet/portfolio').catch(() => null);
      if (w) setBalance(w.data.balance);
      setToast({ msg: `✓ Featured for ${chosenTier.days} day${chosenTier.days === 1 ? '' : 's'}!` });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not purchase Featured slot.');
    } finally {
      setSubmitting(false);
    }
  };

  if (tiers === null || mySlot === undefined) {
    return <AppShell><div className="loading-shell">loading…</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="fp-shell" data-testid="featured-purchase-page">
        <button className="back-btn" onClick={() => navigate('/manage-clan')} data-testid="fp-back-btn" aria-label="Back">
          <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>

        <h1 className="fp-heading">Get <span>Featured</span> on Home</h1>
        <p className="fp-sub">
          Pin your Clan to the top of every user's Home feed. Verified creators only.
        </p>

        {!user?.verified && (
          <div className="fp-warn" data-testid="fp-verify-warn">
            <b>You need to be verified first.</b> Get your ✓ at{' '}
            <a onClick={() => navigate('/verify')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>/verify</a>{' '}before buying a Featured slot.
          </div>
        )}

        {mySlot && (
          <div className="fp-active-card" data-testid="fp-active-card">
            <div className="fp-active-title">✨ You're currently featured</div>
            <div className="fp-active-details">
              Expires in <b>{mySlot.remaining_hours} hours</b> ({new Date(mySlot.expires_at).toLocaleString()}).
              You've paid <b>{mySlot.pts_paid.toLocaleString()} pts</b> across <b>{mySlot.duration_days} days</b>.
            </div>
            <div className="fp-active-hint">Buy more days below to extend your slot.</div>
          </div>
        )}

        <div className="fp-tier-grid" data-testid="fp-tier-grid">
          {tiers.map((t) => {
            const active = Number(selected) === Number(t.days);
            const affordable = balance !== null && balance >= Number(t.pts);
            return (
              <button
                key={t.days}
                type="button"
                className={`fp-tier${active ? ' active' : ''}${!affordable ? ' unaffordable' : ''}`}
                onClick={() => setSelected(Number(t.days))}
                data-testid={`fp-tier-${t.days}`}
                disabled={!user?.verified}
              >
                <div className="fp-tier-days">{t.label || `${t.days} day${t.days === 1 ? '' : 's'}`}</div>
                <div className="fp-tier-price">{Number(t.pts).toLocaleString()} pts</div>
                <div className="fp-tier-desc">{t.description}</div>
                {t.days === 7 && <div className="fp-tier-badge">Best value</div>}
                {!affordable && balance !== null && <div className="fp-tier-warn">Need {Number(t.pts) - balance} more pts</div>}
              </button>
            );
          })}
        </div>

        <div className="fp-summary" data-testid="fp-summary">
          <div className="fp-summary-row">
            <span>Your balance</span>
            <span data-testid="fp-balance">{balance === null ? '—' : `${balance.toLocaleString()} pts`}</span>
          </div>
          {chosenTier && (
            <>
              <div className="fp-summary-row">
                <span>Cost of {chosenTier.days} day{chosenTier.days === 1 ? '' : 's'}</span>
                <span>−{Number(chosenTier.pts).toLocaleString()} pts</span>
              </div>
              <div className="fp-summary-row fp-summary-total">
                <span>Balance after</span>
                <span data-testid="fp-balance-after">
                  {balance === null ? '—' : `${(balance - Number(chosenTier.pts)).toLocaleString()} pts`}
                </span>
              </div>
            </>
          )}
        </div>

        {err && <p className="fp-err" data-testid="fp-error">{err}</p>}

        <button
          className="fp-buy-btn"
          disabled={!chosenTier || !canAfford || submitting || !user?.verified}
          onClick={purchase}
          data-testid="fp-buy-btn"
        >
          {submitting
            ? 'Processing…'
            : !user?.verified
              ? 'Get verified first'
              : !canAfford
                ? 'Not enough Points'
                : mySlot
                  ? `Extend by ${chosenTier?.days} day${chosenTier?.days === 1 ? '' : 's'}`
                  : `Buy Featured Slot · ${Number(chosenTier?.pts || 0).toLocaleString()} pts`}
        </button>
      </div>
      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </AppShell>
  );
}
